import classNames from 'classnames';
import { useRouter } from 'next/router';
import type { FC } from 'react';

import LogoWhite from '@/assets/logo-mindpal.svg';
import LogoPrimary from '@/assets/logo-primary.svg';
import NavigationButton from '@/components/NavigationButton';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { useHiddenHeader } from '@/hooks/useHiddenHeader';

import { useNavigation } from '../useNavigation';
import type { DropdownColumn } from './NavDropdown';
import NavDropdown from './NavDropdown';

type Props = {
  theme?: 'dark' | 'light';
};

const isColumnActive = (columns: DropdownColumn[], pathname: string) =>
  columns.some((column) =>
    column.links.some(
      (link) =>
        link.linkType === 'internal' &&
        typeof link.href === 'string' &&
        pathname.startsWith(link.href)
    )
  );

const DesktopHeader: FC<Props> = ({ theme = 'dark' }) => {
  const { show, isOnTop } = useHiddenHeader();
  const { pathname } = useRouter();
  const navigation = useNavigation();

  const inactiveTextClassName =
    theme === 'light' ? 'text-jb-neutral-1000' : 'text-white';

  return (
    <header
      className={classNames(
        'fixed left-0 top-0 z-40 hidden w-full px-10 transition-all duration-300 lg:flex',
        {
          'bg-gradient-to-tr from-black/90 to-secondary-900/90':
            !isOnTop && theme === 'dark',
          'bg-jb-neutral-100 shadow-md': !isOnTop && theme === 'light',
          '-translate-y-full pointer-events-none': !show && !isOnTop,
          'py-8': isOnTop,
          'py-4': !isOnTop,
        }
      )}
    >
      <NavigationMenu
        aria-label="Main navigation"
        className="flex w-full flex-row items-center justify-between"
      >
        {/* Full page reload is intentional: routes through nginx to the Lovable homepage */}
        {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
        <a
          href="/"
          aria-label="MindPal – go to homepage"
          className="inline-block transition-transform hover:scale-105"
        >
          <div className="w-[140px]">
            {theme === 'light' ? <LogoPrimary /> : <LogoWhite />}
          </div>
        </a>

        <NavigationMenuList className="items-center gap-4">
          {navigation.map(({ label, columns }) => {
            const isActive = isColumnActive(columns, pathname);
            return (
              <NavigationMenuItem key={label}>
                <NavigationMenuTrigger
                  className={classNames(
                    'rounded transition-colors hover:bg-white/10 data-[state=open]:bg-white/10 data-[state=open]:font-semibold data-[state=open]:text-jb-primary-400',
                    isActive
                      ? 'font-semibold text-jb-primary-400'
                      : `font-normal ${inactiveTextClassName}`
                  )}
                >
                  {label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="flex w-full justify-end">
                  <NavDropdown label={label} columns={columns} theme={theme} />
                </NavigationMenuContent>
              </NavigationMenuItem>
            );
          })}

          <NavigationButton
            href="/?contact=true"
            isInternalLink
            variant="unstyled"
            className="flex h-12 items-center gap-2 rounded-lg bg-jb-primary-500 px-4 text-h5 font-semibold text-white transition-colors hover:bg-jb-primary-400"
            suffixIcon="material-symbols:arrow-forward"
          >
            Let&apos;s talk
          </NavigationButton>
        </NavigationMenuList>
      </NavigationMenu>
    </header>
  );
};

export default DesktopHeader;
